import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useUser, isDoctorProfile, isPartnerProfile } from "../contexts/UserContext";
import { useNotifications } from "../contexts/NotificationContext";
import { NavItem, UserDisplayInfo } from "../types/navbar.types";
import {
  FALLBACK_NAV_ITEMS,
  FALLBACK_MOBILE_NAV_ITEMS,
} from "../constants/navbar.constants";
import {
  isValidPath,
  isValidUUID,
  sanitizeInput,
  validateEmail,
  validateAvatarUrl,
  generateInitials,
  getCsrfToken,
} from "../utils/security";

export const useNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAuthenticated } = useUser();
  const { notifications, markAsRead, markAllAsRead, unreadCount } = useNotifications();

  const [navItems, setNavItems] = useState<NavItem[]>(FALLBACK_NAV_ITEMS);
  const [mobileNavItems, setMobileNavItems] = useState<NavItem[]>(FALLBACK_MOBILE_NAV_ITEMS);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lastNavigationRef = useRef<number>(0);
  const logoutAttemptRef = useRef<number>(0);

  // TODO: BACKEND - Load navigation items from GET /api/user/navigation
  useEffect(() => {
    let isMounted = true;

    const loadNavigation = async () => {
      try {
        // const response = await apiCall<NavigationConfig>("/api/user/navigation");
        // if (isMounted && response.success && response.data) {
        //   const desktopValid = response.data.desktop.filter(item => isValidPath(item.path));
        //   const mobileValid = response.data.mobile.filter(item => isValidPath(item.path));
        //   setNavItems(desktopValid.length > 0 ? desktopValid : FALLBACK_NAV_ITEMS);
        //   setMobileNavItems(mobileValid.length > 0 ? mobileValid : FALLBACK_MOBILE_NAV_ITEMS);
        // }
      } catch {
        setNavItems(FALLBACK_NAV_ITEMS);
        setMobileNavItems(FALLBACK_MOBILE_NAV_ITEMS);
      }
    };

    loadNavigation();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      navigate("/login", { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  const handleNavigation = useCallback(
    (path: string) => {
      const now = Date.now();
      if (now - lastNavigationRef.current < 300) return;
      lastNavigationRef.current = now;

      if (!isValidPath(path)) {
        setError("Invalid navigation destination");
        return;
      }

      try {
        navigate(path);
        setError(null);
      } catch {
        setError("Navigation failed. Please try again.");
      }
    },
    [navigate]
  );

  const handleLogout = useCallback(async () => {
    const now = Date.now();
    if (now - logoutAttemptRef.current < 1000) return;
    logoutAttemptRef.current = now;

    try {
      setIsLoggingOut(true);
      setError(null);

      // TODO: BACKEND - POST /api/auth/logout with CSRF token
      // const csrfToken = getCsrfToken();
      // const response = await apiCall("/api/auth/logout", {
      //   method: "POST",
      //   body: JSON.stringify({ csrfToken }),
      // });

      logout();
      sessionStorage.removeItem("csrf-token");
      navigate("/login", { replace: true });
    } catch {
      logout();
      navigate("/login", { replace: true });
    } finally {
      setIsLoggingOut(false);
    }
  }, [logout, navigate]);

  const handleNotificationClick = useCallback(
    (notificationId: string) => {
      if (!isValidUUID(notificationId)) return;

      try {
        markAsRead(notificationId);
        // TODO: BACKEND - POST /api/notifications/{id}/read
      } catch {
        // Notification read failed silently
      }
    },
    [markAsRead]
  );

  const handleMarkAllAsRead = useCallback(() => {
    try {
      markAllAsRead();
      // TODO: BACKEND - POST /api/notifications/mark-all-read
    } catch {
      // Mark all failed silently
    }
  }, [markAllAsRead]);

  const userDisplayInfo = useMemo<UserDisplayInfo>(() => {
  if (!user) {
    return {
      displayName: "Guest",
      initials: "G",
      displayEmail: "guest@example.com",
      avatar: null,
    };
  }

  let displayName = sanitizeInput(user.name, 50);
  let displayEmail = validateEmail(user.email);
  let rawAvatar: string | null | undefined = user.avatar;

  if (user.profile) {
    if (isDoctorProfile(user.profile)) {
      displayName = sanitizeInput(
        user.profile.fullName || user.name,
        50
      );

      displayEmail = validateEmail(
        user.profile.email || user.email
      );

      rawAvatar =
        user.profile.avatar ??
        user.avatar;
    } else if (isPartnerProfile(user.profile)) {
      displayName = sanitizeInput(
        user.profile.facilityName || user.name,
        50
      );

      displayEmail = validateEmail(
        user.profile.email || user.email
      );

      rawAvatar =
        user.profile.avatar ??
        user.avatar;
    }
  }

  const avatar: string | null =
    typeof rawAvatar === "string" &&
    (
      rawAvatar.startsWith("data:image/") ||
      rawAvatar.startsWith("blob:")
    )
      ? rawAvatar
      : validateAvatarUrl(
          rawAvatar ?? undefined
        ) ?? null;

  return {
    displayName,
    initials: generateInitials(displayName),
    displayEmail,
    avatar,
  };
}, [user]);

  const isActive = useCallback(
    (path: string) => location.pathname === path && isValidPath(path),
    [location.pathname]
  );

  const clearError = useCallback(() => setError(null), []);

  return {
    // State
    navItems,
    mobileNavItems,
    isLoggingOut,
    error,
    notifications,
    unreadCount,
    userDisplayInfo,
    isAuthenticated,
    user,
    location,

    // Actions
    handleNavigation,
    handleLogout,
    handleNotificationClick,
    handleMarkAllAsRead,
    isActive,
    clearError,
    navigate,
  };
};